// src/pages/Bots.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  Select,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiActivity,
  FiLink,
  FiClock
} from 'react-icons/fi';
import './Bots.css';

const Bots = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bots, setBots] = useState([]);
  const [loadingBots, setLoadingBots] = useState({});
  const toast = useToast();

  // Chakra UI theme colors
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.600', 'gray.300');
  const textTertiary = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    // Get bots from localStorage (from login response)
    const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
    const userBots = userInfo.bots || [];
    
    console.log('Loading bots from localStorage:', userBots);
    
    // Transform the data to match component expectations
    const transformedBots = userBots.map(bot => ({
      id: bot.id,
      name: bot.name || `Bot ${bot.id}`,
      description: bot.description || 'No description available',
      status: bot.active ? 'active' : 'inactive',
      active: bot.active !== false,
      connections: Array.isArray(bot.connections) ? bot.connections.length : (bot.connection_ids ? bot.connection_ids.length : 0),
      lastActive: bot.last_active || 'Unknown',
      originalData: bot,
      activitiesCount: bot.activities ? bot.activities.length : 0,
      providers: bot.providers || [],
      state: bot.state || (bot.active ? 'Activo' : 'Inactivo')
    }));
    
    setBots(transformedBots);
  }, []);

  // Function to handle bot status toggle
  const handleSaveActive = async (e, botId) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setLoadingBots(prev => ({ ...prev, [botId]: true }));
      
      const ui = localStorage.getItem('user_info');
      if (!ui) {
        throw new Error('User information not found. Please log in again.');
      }
      
      const userInfo = JSON.parse(ui);
      const email = userInfo.email;
      
      if (!email) {
        throw new Error('User email not found. Please log in again.');
      }
      
      const currentBot = bots.find(bot => bot.id === botId);
      if (!currentBot) {
        throw new Error('Bot not found');
      }
      
      const newActiveStatus = !currentBot.active;
      
      const updateData = {
        id: botId,
        email: email,
        active: newActiveStatus
      };
      
      const response = await fetch(
        `85583adb-06b4-41dd-839f-241c1405c5ae.clouding.host/api/notto/bots/${botId}/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setBots(prevBots => 
          prevBots.map(bot => 
            bot.id === botId 
              ? { 
                  ...bot, 
                  active: newActiveStatus,
                  status: newActiveStatus ? 'active' : 'inactive',
                  state: newActiveStatus ? 'Activo' : 'Inactivo'
                }
              : bot
          )
        );
        
        const updatedUserInfo = {
          ...userInfo,
          bots: userInfo.bots.map(bot => 
            bot.id === botId 
              ? { ...bot, active: newActiveStatus }
              : bot
          )
        };
        localStorage.setItem('user_info', JSON.stringify(updatedUserInfo));
        
        toast({
          title: 'Éxito',
          description: `Bot ${newActiveStatus ? 'activado' : 'desactivado'} correctamente`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(data.message || 'Failed to update bot');
      }
    } catch (error) {
      console.error('Error updating bot:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error de red. Por favor intenta de nuevo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingBots(prev => {
        const updated = { ...prev };
        delete updated[botId];
        return updated;
      });
    }
  };

  // Format date helper
  const formatLastActive = (lastActive) => {
    if (!lastActive || lastActive === 'Unknown') return 'No disponible';
    
    try {
      const date = new Date(lastActive);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'Hoy';
      } else if (diffDays === 1) {
        return 'Ayer';
      } else if (diffDays < 7) {
        return `${diffDays}d`;
      } else {
        return date.toLocaleDateString('es-ES', { 
          day: '2-digit', 
          month: '2-digit' 
        });
      }
    } catch (error) {
      return lastActive;
    }
  };

  // Filter bots
  const filteredBots = bots.filter(bot => {
    const matchesSearch = (bot.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (bot.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || bot.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <Box className="bots-page" bg={bg} minH="100vh" p={6}>
      <Container maxW="container.xl">
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} className="bots-header">
          <Heading 
            size="xl" 
            color={textPrimary}
            fontWeight="600"
          >
            Mis Bots ({bots.length})
          </Heading>
          <Button
            leftIcon={<Icon as={FiPlus} />}
            colorScheme="blue"
            size="lg"
            className="create-bot-btn"
            onClick={() => navigate('/templates')}
          >
            Crear Nuevo Bot
          </Button>
        </Flex>

        {/* Filters */}
        <Flex 
          gap={4} 
          mb={6}
          direction={{ base: 'row', md: 'row' }}
          className="bots-filters"
        >
          <Input 
            placeholder="Buscar bot..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            flex={1}
            bg={inputBg}
            borderColor={borderColor}
            color={textPrimary}
            _placeholder={{ color: textTertiary }}
            className="search-input"
          />
          <Select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            minW="200px"
            bg={inputBg}
            borderColor={borderColor}
            color={textPrimary}
            className="filter-select"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </Select>
        </Flex>

        {/* Bots List */}
        {filteredBots.length > 0 ? (
          <VStack spacing={4} align="stretch" className="bots-list">
            {filteredBots.map((bot, index) => (
              <Box
                key={bot.id}
                className={`bot-card ${bot.active ? 'active' : 'inactive'} bot-card-animate`}
                style={{ animationDelay: `${index * 0.1}s` }}
                borderColor={borderColor}
                p={6}
              >
                <Flex 
                  direction={{ base: 'column', lg: 'row' }} 
                  align={{ base: 'stretch', lg: 'center' }}
                  gap={6}
                >
                  {/* Bot Info Section */}
                  <Box flex="1" minW="0">
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <VStack align="flex-start" spacing={1}>
                        <Heading 
                          size="md" 
                          color={textPrimary} 
                          fontWeight="600"
                          className="bot-name"
                        >
                          {bot.name}
                        </Heading>
                        <Text 
                          color={textSecondary} 
                          fontSize="sm"
                          className="bot-description"
                        >
                          {bot.description}
                        </Text>
                      </VStack>
                      <Badge
                        onClick={(e) => handleSaveActive(e, bot.id)}
                        colorScheme={bot.active ? 'green' : 'red'}
                        className="status-badge"
                        cursor="pointer"
                      >
                        {loadingBots[bot.id] ? (
                          <Spinner size="xs" />
                        ) : (
                          bot.state
                        )}
                      </Badge>
                    </Flex>

                    {/* Bot Stats */}
                    <HStack spacing={6} className="bot-stats">
                      <HStack spacing={2}>
                        <Icon as={FiLink} color="blue.400" boxSize={4} />
                        <Text fontSize="sm" color={textSecondary}>
                          <Text as="span" fontWeight="600" color={textPrimary}>
                            {bot.connections}
                          </Text>{' '}
                          conexiones
                        </Text>
                      </HStack>
                      
                      <HStack spacing={2}>
                        <Icon as={FiActivity} color="green.400" boxSize={4} />
                        <Text fontSize="sm" color={textSecondary}>
                          <Text as="span" fontWeight="600" color={textPrimary}>
                            {bot.activitiesCount}
                          </Text>{' '}
                          actividades
                        </Text>
                      </HStack>
                      
                      <HStack spacing={2}>
                        <Icon as={FiClock} color="purple.400" boxSize={4} />
                        <Text fontSize="sm" color={textSecondary}>
                          {formatLastActive(bot.lastActive)}
                        </Text>
                      </HStack>
                    </HStack>
                  </Box>

                  {/* Actions Section */}
                  <Box>
                    <Flex gap={2} flexWrap="wrap" className="bot-actions">
                      <Button
                        size="sm"
                        className="action-button edit-btn"
                        onClick={() => navigate(`/bots/${bot.id}`)}
                      >
                        ✏️ Editar
                      </Button>
                      
                      <Button
                        size="sm"
                        className="action-button view-btn"
                        onClick={() => navigate(`/bots/${bot.id}`)}
                      >
                        👁️ Ver
                      </Button>
                      
                      <Button
                        size="sm"
                        className="action-button flow-btn"
                        onClick={() => navigate(`/flux/${bot.id}`)}
                      >
                        🌊 Flujo
                      </Button>
                    </Flex>
                  </Box>
                </Flex>
              </Box>
            ))}
          </VStack>
        ) : (
          <Box 
            className="empty-state"
            bg={cardBg}
            borderColor={borderColor}
            p={12}
            textAlign="center"
            maxW="lg"
            mx="auto"
          >
            {bots.length === 0 ? (
              <VStack spacing={6}>
                <Alert 
                  status="info" 
                  variant="subtle" 
                  borderRadius="lg"
                  className="empty-alert"
                >
                  <AlertIcon color="blue.500" />
                  <Box textAlign="left">
                    <AlertTitle color={textPrimary}>No tienes bots creados</AlertTitle>
                    <AlertDescription color={textSecondary}>
                      Crea tu primer bot para comenzar a automatizar tus conversaciones.
                    </AlertDescription>
                  </Box>
                </Alert>
                <Button
                  leftIcon={<Icon as={FiPlus} />}
                  colorScheme="blue"
                  size="lg"
                  className="create-first-bot-btn"
                  onClick={() => navigate('/templates')}
                >
                  Crear Mi Primer Bot
                </Button>
              </VStack>
            ) : (
              <Text color={textSecondary} fontSize="lg">
                No se encontraron bots que coincidan con los criterios de búsqueda.
              </Text>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Bots;